import { __extends } from "tslib";
import { Matrix3D, Transform, OrthographicProjection } from '@awayjs/core';
import { Image2D } from '@awayjs/stage';
import { View } from '@awayjs/view';
import { RenderGroup, DepthRenderer } from '@awayjs/renderer';
import { ShadowTexture2D } from '../textures/ShadowTexture2D';
import { ShadowMapperBase, _Shader_ShadowMapperBase } from './ShadowMapperBase';
var DirectionalShadowMapper = /** @class */ (function (_super) {
    __extends(DirectionalShadowMapper, _super);
    function DirectionalShadowMapper(image2D) {
        if (image2D === void 0) { image2D = null; }
        var _this = _super.call(this) || this;
        _this._lightOffset = 10000;
        _this._snap = 64;
        _this._size = 2048;
        _this._image2D = image2D || new Image2D(_this._size, _this._size);
        _this._textureMap = new ShadowTexture2D(_this._image2D);
        _this.iAddTexture(_this._textureMap);
        _this._cullPlanes = [];
        _this._overallDepthProjection = new OrthographicProjection();
        _this._overallDepthProjection.transform = new Transform();
        _this._localFrustum = [];
        _this._matrix = new Matrix3D();
        return _this;
    }
    Object.defineProperty(DirectionalShadowMapper.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return DirectionalShadowMapper.assetType;
        },
        enumerable: true,
        configurable: true
    });
    DirectionalShadowMapper.prototype.dispose = function () {
        _super.prototype.dispose.call(this);
        this._textureMap.image = null;
        this._textureMap = null;
        this._image2D = null;
    };
    Object.defineProperty(DirectionalShadowMapper.prototype, "snap", {
        get: function () {
            return this._snap;
        },
        set: function (value) {
            this._snap = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DirectionalShadowMapper.prototype, "lightOffset", {
        get: function () {
            return this._lightOffset;
        },
        set: function (value) {
            this._lightOffset = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DirectionalShadowMapper.prototype, "depthProjection", {
        //@arcane
        get: function () {
            return this._overallDepthProjection.viewMatrix3D;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DirectionalShadowMapper.prototype, "depth", {
        //@arcane
        get: function () {
            return this._maxZ - this._minZ;
        },
        enumerable: true,
        configurable: true
    });
    DirectionalShadowMapper.prototype._updateSize = function () {
        this._image2D._setSize(this._size, this._size);
    };
    /**
     *
     * @param projection
     * @private
     */
    DirectionalShadowMapper.prototype._updateProjection = function (projection) {
        this._updateProjectionFromFrustumCorners(projection, projection.viewFrustumCorners, this._matrix);
        this._overallDepthProjection.frustumMatrix3D = this._matrix;
        this._updateCullPlanes(projection);
    };
    //@override
    DirectionalShadowMapper.prototype._renderMap = function (partition, renderGroup) {
        var depthRenderer = RenderGroup
            .getInstance(new View(null, renderGroup.stage), DepthRenderer)
            .getRenderer(partition);
        depthRenderer.cullPlanes = this._cullPlanes;
        depthRenderer.view.preservePixelRatio = false;
        depthRenderer.view.target = this._image2D;
        depthRenderer.view.projection = this._overallDepthProjection;
        depthRenderer.render();
    };
    /**
     *
     * @param projection
     * @private
     */
    DirectionalShadowMapper.prototype._updateCullPlanes = function (projection) {
        var lightFrustumPlanes = this._overallDepthProjection.viewFrustumPlanes;
        var viewFrustumPlanes = projection.viewFrustumPlanes;
        this._cullPlanes.length = 4;
        this._cullPlanes[0] = lightFrustumPlanes[0];
        this._cullPlanes[1] = lightFrustumPlanes[1];
        this._cullPlanes[2] = lightFrustumPlanes[2];
        this._cullPlanes[3] = lightFrustumPlanes[3];
        var dir = this._light.sceneDirection;
        var dirX = dir.x;
        var dirY = dir.y;
        var dirZ = dir.z;
        var j = 4;
        for (var i = 0; i < 6; ++i) {
            var plane = viewFrustumPlanes[i];
            if (plane.a * dirX + plane.b * dirY + plane.c * dirZ < 0)
                this._cullPlanes[j++] = plane;
        }
    };
    /**
     *
     * @param projection
     * @param matrix
     * @private
     */
    DirectionalShadowMapper.prototype._updateProjectionFromFrustumCorners = function (projection, corners, matrix) {
        var x, y, z;
        var minX, minY;
        var maxX, maxY;
        var i;
        var pos = projection.transform.matrix3D.position;
        var dir = this._light.sceneDirection;
        this._overallDepthProjection.transform.matrix3D = this._light.transform.matrix3D;
        x = Math.floor((pos.x - dir.x * this._lightOffset) / this._snap) * this._snap;
        y = Math.floor((pos.y - dir.y * this._lightOffset) / this._snap) * this._snap;
        z = Math.floor((pos.z - dir.z * this._lightOffset) / this._snap) * this._snap;
        this._overallDepthProjection.transform.moveTo(x, y, z);
        this._overallDepthProjection.transform.inverseMatrix3D.transformVectors(corners, this._localFrustum);
        minX = maxX = this._localFrustum[0];
        minY = maxY = this._localFrustum[1];
        this._maxZ = this._localFrustum[2];
        i = 3;
        while (i < 24) {
            x = this._localFrustum[i];
            y = this._localFrustum[i + 1];
            z = this._localFrustum[i + 2];
            if (x < minX)
                minX = x;
            if (x > maxX)
                maxX = x;
            if (y < minY)
                minY = y;
            if (y > maxY)
                maxY = y;
            if (z > this._maxZ)
                this._maxZ = z;
            i += 3;
        }
        this._minZ = 1;
        var w = maxX - minX;
        var h = maxY - minY;
        var d = 1 / (this._maxZ - this._minZ);
        if (minX < 0)
            minX -= this._snap; // because int() rounds up for < 0
        if (minY < 0)
            minY -= this._snap;
        minX = Math.floor(minX / this._snap) * this._snap;
        minY = Math.floor(minY / this._snap) * this._snap;
        var snap2 = 2 * this._snap;
        w = Math.floor(w / snap2 + 2) * snap2;
        h = Math.floor(h / snap2 + 2) * snap2;
        maxX = minX + w;
        maxY = minY + h;
        w = 1 / w;
        h = 1 / h;
        var raw = matrix._rawData;
        raw[0] = 2 * w;
        raw[5] = 2 * h;
        raw[10] = d;
        raw[12] = -(maxX + minX) * w;
        raw[13] = -(maxY + minY) * h;
        raw[14] = -this._minZ * d;
        raw[15] = 1;
        raw[1] = raw[2] = raw[3] = raw[4] = raw[6] = raw[7] = raw[8] = raw[9] = raw[11] = 0;
        matrix.invalidatePosition();
    };
    DirectionalShadowMapper.assetType = '[asset DirectionalShadowMapper]';
    return DirectionalShadowMapper;
}(ShadowMapperBase));
export { DirectionalShadowMapper };
import { ShaderBase } from '@awayjs/renderer';
/**
 * ShadowChunkBase provides an abstract method for simple (non-wrapping) shadow map methods.
 */
var _Shader_DirectionalShadowMapper = /** @class */ (function (_super) {
    __extends(_Shader_DirectionalShadowMapper, _super);
    function _Shader_DirectionalShadowMapper() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * @inheritDoc
     */
    _Shader_DirectionalShadowMapper.prototype._initVO = function (chunkVO) {
        _super.prototype._initVO.call(this, chunkVO);
        chunkVO.needsView = true;
        chunkVO.needsGlobalVertexPos = true;
    };
    /**
     * @inheritDoc
     */
    _Shader_DirectionalShadowMapper.prototype._initConstants = function () {
        _super.prototype._initConstants.call(this);
        var vertexData = this._shader.vertexConstantData;
        var index = this._vertexScalingIndex;
        vertexData[index] = .5;
        vertexData[index + 1] = .5;
        vertexData[index + 2] = 0.0;
        vertexData[index + 3] = 1.0;
        this._depthProjectionMatrix = new Matrix3D(new Float32Array(this._shader.vertexConstantData.buffer, (index + 4) * 4, 16));
    };
    /**
     * @inheritDoc
     */
    _Shader_DirectionalShadowMapper.prototype._getVertexCode = function (regCache, sharedRegisters) {
        var code = '';
        var temp = regCache.getFreeVertexVectorTemp();
        var dataReg = regCache.getFreeVertexConstant();
        this._vertexScalingIndex = dataReg.index * 4;
        var depthMapProj = regCache.getFreeVertexConstant();
        regCache.getFreeVertexConstant();
        regCache.getFreeVertexConstant();
        regCache.getFreeVertexConstant();
        this._depthMapCoordReg = regCache.getFreeVarying();
        code += 'm44 ' + temp + ', ' + sharedRegisters.globalPositionVertex + ', ' + depthMapProj + '\n' +
            'div ' + temp + ', ' + temp + ', ' + temp + '.w\n' +
            'mul ' + temp + '.xy, ' + temp + '.xy, ' + dataReg + '.xy\n' +
            'add ' + this._depthMapCoordReg + ', ' + temp + ', ' + dataReg + '.xxwz\n';
        return code;
    };
    /**
     * @inheritDoc
     */
    _Shader_DirectionalShadowMapper.prototype._activate = function () {
        _super.prototype._activate.call(this);
        var vertexData = this._shader.vertexConstantData;
        vertexData[this._vertexScalingIndex + 3] = -1 / (this._mapper.depth * this._mapper.epsilon);
        this._depthProjectionMatrix.copyFrom(this._mapper.depthProjection, true);
    };
    return _Shader_DirectionalShadowMapper;
}(_Shader_ShadowMapperBase));
export { _Shader_DirectionalShadowMapper };
ShaderBase.registerAbstraction(_Shader_DirectionalShadowMapper, DirectionalShadowMapper);
